// The page title block every public page but Home opens with
// (PROTO build.py `opener()` + site.js's word-safe letter split and rise).
import { Fragment, useRef } from 'react';
import { gsap, reduceMotion, useGsapContext } from './motion.js';

export interface OpenerProps {
  /** One `<span class="chars">` per line, joined with `<br>` — pass the
   * prototype's `title.split('|')` segments, e.g. `['About', 'Clan Heart']`. */
  lines: string[];
  lede?: string;
}

/** Splits text into word-safe `<span class="word">` spans of per-letter
 * `<span class="ch">` spans — the same shape site.js builds by mutating
 * `innerHTML` after the fact, built directly as JSX here so there's nothing
 * to hydrate or re-run on re-render. */
function splitChars(text: string, keyPrefix: string) {
  return text.split(/(\s+)/).map((chunk, i) => {
    if (!chunk.trim()) return ' ';
    return (
      <span className="word" key={`${keyPrefix}-${i}`}>
        {Array.from(chunk).map((ch, j) => (
          <span className="ch" key={j}>
            {ch}
          </span>
        ))}
      </span>
    );
  });
}

export function Opener({ lines, lede }: OpenerProps) {
  const ref = useRef<HTMLElement | null>(null);

  // Scoped to this <section> via gsap.context, so the plain selector text
  // below only ever matches this Opener's own letters — two Openers never
  // interfere with each other's tweens, and ctx.revert() on unmount leaves
  // every letter fully visible again (gsap.from's end state is the DOM's
  // natural one) rather than stuck mid-animation.
  useGsapContext(
    ref,
    () => {
      if (reduceMotion) return;
      gsap.from('.giant .ch', { yPercent: 105, duration: 1.2, ease: 'expo.out', stagger: 0.03, delay: 0.1 });
      // Only tween the lede if this Opener actually rendered one — an empty
      // selector logs a "target not found" console warning otherwise.
      if (ref.current?.querySelector('[data-intro]')) {
        gsap.from('[data-intro]', { y: 24, opacity: 0, duration: 1.1, ease: 'expo.out', stagger: 0.1, delay: 0.45 });
      }
    },
    [],
  );

  return (
    <section className="opener wrap" ref={ref}>
      <h1 className="giant">
        {lines.map((line, i) => (
          <Fragment key={i}>
            {i > 0 && <br />}
            <span className="chars">{splitChars(line, `l${i}`)}</span>
          </Fragment>
        ))}
      </h1>
      {lede && (
        <p className="lede" data-intro>
          {lede}
        </p>
      )}
    </section>
  );
}
